import { useCallback, useEffect, useRef, useState } from "react";

interface SheetProps {
  // The id of the heading that names the sheet.
  labelledBy: string;
  onClose: () => void;
  className?: string;
  // The contents, handed a way to close the sheet with its exit animation.
  children: (close: () => void) => React.ReactNode;
}

// How long the sheet takes to leave; matches the closing animation in daily.css.
const CLOSES_IN = 280;

// A sheet rising from the bottom on a phone, a dialog on desktop, with
// whatever is behind still showing through the dim. Tapping the dim or
// pressing Escape closes it.
export const Sheet = ({ labelledBy, onClose, className, children }: SheetProps) => {
  // It leaves the way it came in: the exit plays first, then it closes.
  const [closing, setClosing] = useState(false);
  const timer = useRef<number>();

  const close = useCallback(() => {
    if (timer.current !== undefined) return;
    setClosing(true);
    timer.current = window.setTimeout(onClose, CLOSES_IN);
  }, [onClose]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [close]);

  return (
    <div
      className={`ds ds-help ${closing ? "ds-help--closing" : ""} ${className ?? ""}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        className="ds-help__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
      >
        <span className="ds-help__handle" aria-hidden="true" />
        {children(close)}
      </div>
    </div>
  );
};
